import React, { useState, useRef } from 'react';
import { Upload, X, Image, Loader2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { LoadingInline } from '../ui/LoadingInline';
import { LogoDisplay } from './LogoDisplay';
import { uploadProjectLogo, removeProjectLogo } from '../../services/api';

interface LogoUploadProps {
  projectId: string;
  projectName: string;
  currentLogo?: string;
  onLogoChange?: (logoPath: string | null) => void;
  className?: string;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  projectId,
  projectName,
  currentLogo,
  onLogoChange,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadLogo = async (file: File) => {
    try {
      setUploading(true);
      setError(null);

      const response = await uploadProjectLogo(projectId, file);
      
      setUploadSuccess(true);
      onLogoChange?.(response.data.data.logoPath);
      
      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (err: any) {
      let errorMessage = 'Erro ao fazer upload do logo';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente ou contate o administrador.';
      } else if (err.response?.status === 413) {
        errorMessage = 'Arquivo muito grande. Tamanho máximo: 5MB.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Arquivo inválido. Use apenas imagens JPG, PNG, GIF ou SVG.';
      }
      
      setError(errorMessage);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      setUploading(true);
      setError(null);

      await removeProjectLogo(projectId);
      onLogoChange?.(null);
    } catch (err: any) {
      let errorMessage = 'Erro ao remover logo';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente ou contate o administrador.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Logo não encontrado ou projeto não existe.';
      }
      
      setError(errorMessage);
      console.error('Remove error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Formato de arquivo não suportado. Use apenas JPEG, PNG, GIF ou SVG.');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Arquivo muito grande. Tamanho máximo: 5MB.');
      return;
    }

    uploadLogo(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Current Logo Display */}
      {currentLogo && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground-primary">Logo Atual:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={removeLogo}
              disabled={uploading}
              className="border-white/20 hover:bg-white/10 text-foreground-secondary hover:text-foreground-primary h-8"
            >
              <X className="w-3 h-3 mr-1" />
              Remover
            </Button>
          </div>
          <LogoDisplay 
            projectId={projectId}
            logoPath={currentLogo} 
            projectName={projectName} 
            size="medium"
            className="border border-white/10 rounded-lg p-4 bg-white/5"
          />
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${dragActive ? 'border-blue-400 bg-blue-500/10' : 'border-white/20'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 hover:bg-blue-500/10'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!uploading ? openFileSelector : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={uploading}
        />

        {uploading ? (
          <div className="flex flex-col items-center">
            <LoadingInline size="md" message="Fazendo upload" />
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="w-8 h-8 text-foreground-tertiary mb-2" />
            <p className="text-sm font-medium text-foreground-primary mb-1">
              Clique para selecionar ou arraste o arquivo aqui
            </p>
            <p className="text-xs text-foreground-tertiary">
              PNG, JPG, GIF ou SVG - máximo 5MB
            </p>
          </div>
        )}
      </div>

      {/* Success Message */}
      {uploadSuccess && (
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center">
          <Check className="w-4 h-4 text-green-400 mr-2" />
          <span className="text-sm text-green-400">Logo atualizado com sucesso!</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center">
          <X className="w-4 h-4 text-red-400 mr-2" />
          <span className="text-sm text-red-400">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload Instructions */}
      <div className="text-xs text-foreground-tertiary space-y-1">
        <p>• Formatos suportados: PNG, JPG, GIF, SVG</p>
        <p>• Tamanho máximo: 5MB</p>
        <p>• Recomendado: Logo em alta resolução com fundo transparente</p>
      </div>
    </div>
  );
};

export default LogoUpload;